import { Grid } from '@mui/material'
import clsx from 'clsx'
import { MInspector } from '@Models/client/inspection.model'
import React, { useEffect, useState, lazy, Suspense } from 'react'
import ClientInspectionActions from '@ClientActions/inspection.action'
import * as Redux from 'redux'
import { connect } from 'react-redux'
import ClientFirearmActions from '@ClientActions/firearm.action'
import {
    ICommonProduct,
    ICommonQuery,
    MCommonProduct,
} from '@Models/common.model'
import moment from 'moment'
import { ToastServices } from '@Services/toast.service'
import { APIServices } from '@Services/api.service'
// import UserInfoBox from '@Components/Box/UserInfoBox/UserInfoBox'
import CustomTabs from '@Components/CustomTabs/CustomTabs/CustomTabs'
import TabPanel from '@Components/CustomTabs/TabPanel/TabPanel'
import BaseButton from '@Components/Form/BaseButton/BaseButton'
import PageHeader from '@Components/Pages/PageHeader/PageHeader'
import Loading from '@Components/Loading/Loading'
import UserInfoBox from '../../../components/Box/UserInfoBox/UserInfoBox'
import InspectorModal from './InspectorModal'
import Firearms from './Firearms'
import Accessories from './Accessories'

const History = lazy(() => import('./history'))

// dummy data
const tabs = ['Upcoming Inspection', 'History']

// models section
interface IInspection {
    // redux
    createInspection: (body: any) => Promise<any>
    getFirearms: (type: ICommonQuery) => void
    getAccessories: (type: ICommonQuery) => void
    firearms: MCommonProduct[]
    accessories: MCommonProduct[]
    userId: string
}

function Inspection(props: IInspection) {
    // core section
    const {
        createInspection,
        firearms,
        accessories,
        getFirearms,
        getAccessories,
        userId,
    } = props

    // states section
    const [tab, setTab] = useState(0)
    const [showInspectorModal, setShowInspectorModal] = useState(false)
    const [disabled, setDisabled] = useState(false)
    const [selectedInspector, setSelectedInspector] = useState<MInspector>()
    const [favoriteInspectors, setFavoriteInspectors] = useState<MInspector[]>(
        []
    )
    const [inspectorFirearms, setInspectorFirearms] = useState<
        MCommonProduct[]
    >([])
    const [inspectorAccessories, setInspectorAccessories] = useState<
        MCommonProduct[]
    >([])

    // hooks section
    /**
     * get data from API when first load
     */
    useEffect(() => {
        const params = {
            status: 'BOUGHT',
        }
        getFirearms(params)
        // getAccessories(params)
        return () => {
            APIServices.axiosCancelToken()
        }
    }, [])

    /**
     * perform format firearms while firearms change
     */
    useEffect(() => {
        const filteredFirearms = firearms?.map(
            (item: MCommonProduct) => new MCommonProduct(item)
        )
        setInspectorFirearms(filteredFirearms)
        return () => {
            setInspectorFirearms([])
        }
    }, [firearms])

    /**
     * perform format accessories while accessories change
     */
    useEffect(() => {
        const filteredAccessories = accessories?.map(
            (item: MCommonProduct) => new MCommonProduct(item)
        )
        setInspectorAccessories(filteredAccessories)
        return () => {
            setInspectorAccessories([])
        }
    }, [accessories])

    // functions section
    const handleTabChange = (
        event: React.ChangeEvent<any>,
        newValue: number
    ) => {
        setTab(newValue)
    }

    const handleRemoveInspection = (inspectorId: string) => {
        const filteredInspectors = favoriteInspectors?.filter(
            (inspector: MInspector) => inspector?.inspectorId !== inspectorId
        )
        setFavoriteInspectors(filteredInspectors)
    }

    /**
     * handle form submit and do API request to create inspection
     * @param formData inputs value
     * @returns
     */
    const onSubmit = () => {
        const filteredFirearms = inspectorFirearms
            ?.filter((firearm: MCommonProduct) => firearm?.checked)
            ?.map((firearm: MCommonProduct) => firearm?.id)
        const filteredAccessories = inspectorAccessories
            ?.filter((accessory: MCommonProduct) => accessory?.checked)
            ?.map((accessory: MCommonProduct) => accessory?.id)
        if (!filteredFirearms?.length) {
            return ToastServices.error('Please select at least one firearm!')
        }
        if (!selectedInspector?.id) {
            return ToastServices.error('Directory of inspector is required!')
        }

        const scheduledAt = moment().add(1, 'year').toISOString()
        const inspectorId = selectedInspector?.inspectorId
        const itemIds = [...filteredFirearms, ...filteredAccessories]
        const status = 'PENDING'
        const body = { inspectorId, itemIds, userId, scheduledAt, status }
        setDisabled(true)
        createInspection(body)
            .then(() => {
                const resetFirearms = inspectorFirearms?.map(
                    (firearm: MCommonProduct) => {
                        return { ...firearm, checked: false }
                    }
                )
                const resetAccessories = inspectorAccessories?.map(
                    (accessory: MCommonProduct) => {
                        return { ...accessory, checked: false }
                    }
                )
                setDisabled(false)
                setInspectorFirearms(resetFirearms)
                setInspectorAccessories(resetAccessories)
                setSelectedInspector(new MInspector({}))
            })
            .catch(() => setDisabled(false))
    }

    /**
     * handle checked firearms
     * @param action action value
     * @param product firearm
     */
    const handleFirearmActions = (action: string, product: ICommonProduct) => {
        if (action === 'checkbox' && product) {
            const mappedFirearms: any = inspectorFirearms?.map(
                (item: MCommonProduct) => {
                    return {
                        ...item,
                        checked:
                            product?.id === item?.id
                                ? !product?.checked
                                : item?.checked,
                    }
                }
            )
            setInspectorFirearms(mappedFirearms)
        }
    }

    /**
     * handle checked accessories
     * @param action action value
     * @param product accessory
     */
    const handleAccessoryActions = (
        action: string,
        product: ICommonProduct
    ) => {
        if (action === 'checkbox' && product) {
            const mappedAccessories: any = inspectorAccessories?.map(
                (item: MCommonProduct) => {
                    return {
                        ...item,
                        checked:
                            product?.id === item?.id
                                ? !product?.checked
                                : item?.checked,
                    }
                }
            )
            setInspectorAccessories(mappedAccessories)
        }
    }

    /**
     * handle select inspector to do inspection
     * @param inspector inspector obj
     */
    const handleInspectorClicked = (inspector: MInspector) => {
        if (
            selectedInspector &&
            selectedInspector?.inspectorId === inspector?.inspectorId
        ) {
            setSelectedInspector(new MInspector({}))
        } else {
            setSelectedInspector(inspector)
        }
    }

    // main
    return (
        <>
            <InspectorModal
                open={showInspectorModal}
                onClose={() => setShowInspectorModal(false)}
                getSelectedInspectors={(selectedInspectors: MInspector[]) => {
                    setFavoriteInspectors((prevState) => [
                        ...selectedInspectors,
                        ...prevState,
                    ])
                }}
                defaultInspectors={favoriteInspectors}
            />

            <div className="page-inspection">
                <PageHeader title="Inspection" />
                <CustomTabs
                    tabs={tabs}
                    value={tab}
                    onChange={handleTabChange}
                />
                <TabPanel value={tab} index={0}>
                    <div className="page-min-height">
                        <div>
                            <p className="page-title mt-4">
                                Firearms up for inspection
                            </p>
                            <Grid container spacing={2}>
                                <Firearms
                                    data={inspectorFirearms}
                                    firearmActions={handleFirearmActions}
                                />
                            </Grid>
                        </div>
                        {/* <div>
                            <p className="page-title mt-4">
                                Accessories up for inspection
                            </p>
                            <Grid container spacing={2}>
                                <Accessories
                                    data={inspectorAccessories}
                                    accessoryActions={handleAccessoryActions}
                                />
                            </Grid>
                        </div> */}
                        <div>
                            <p className="page-title my-4">
                                Directory of Inspectors
                            </p>
                            <Grid container spacing={3} className="mb-1">
                                {favoriteInspectors?.map(
                                    (inspector: MInspector, index: number) => {
                                        return (
                                            <Grid
                                                item
                                                md={4}
                                                sm={4}
                                                xs={12}
                                                key={+index}
                                            >
                                                <UserInfoBox
                                                    user={inspector}
                                                    className={clsx(
                                                        'user-info-box cursor-pointer'
                                                    )}
                                                    remove
                                                    removeAction={(e: any) => {
                                                        handleRemoveInspection(
                                                            inspector?.inspectorId
                                                        )
                                                    }}
                                                    onClick={(e: any) => {
                                                        handleInspectorClicked(
                                                            inspector
                                                        )
                                                    }}
                                                    selected={
                                                        selectedInspector?.inspectorId ===
                                                        inspector?.inspectorId
                                                    }
                                                />
                                            </Grid>
                                        )
                                    }
                                )}
                                {/* <Grid item sm={4}>
                                        <UserInfoBox />
                                    </Grid> */}
                            </Grid>
                            <BaseButton
                                onClick={() => setShowInspectorModal(true)}
                                className="outline-gray font-weight-bold mt-3"
                                style={{ width: 150 }}
                            >
                                {favoriteInspectors?.length
                                    ? 'Add Other Directory'
                                    : 'Directory'}
                            </BaseButton>
                            {/* <BaseButton
                        className="outline-gray font-weight-bold"
                        style={{ width: 150 }}
                    >
                        Add Other Directory
                    </BaseButton> */}
                        </div>
                        {/* <hr className="page-hr" />
                        <div className="text-right">
                            <BaseButton
                                className="variant-red btn-lg-width"
                                type="submit"
                                onClick={onSubmit}
                                disabled={disabled}
                                isLoading={disabled}
                            >
                                Submit
                            </BaseButton>
                        </div> */}
                    </div>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                    <Suspense fallback={<Loading isLoading />}>
                        <History />
                    </Suspense>
                </TabPanel>
            </div>
        </>
    )
}

// store section
const mapStateToProps = (store: any) => {
    const { inspectorLoading } = store?.client?.inspections
    const { firearms, accessories } = store?.client
    const userId = store?.auth?.user?.id
    return {
        inspectorLoading,
        firearms: firearms?.firearmData,
        accessories: accessories?.accessoriesData,
        userId,
    }
}

const mapDispatchToProps = (dispatch: Redux.Dispatch) => {
    const { createInspection } = ClientInspectionActions
    const { getItems } = ClientFirearmActions

    return {
        getFirearms: (params: any) =>
            dispatch(getItems('FIREARM', params) as any),
        getAccessories: (params: any) =>
            dispatch(getItems('ACCESSORY', params) as any),
        createInspection: (body: any) =>
            dispatch(createInspection(body) as any),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Inspection)
